import axios from "axios";
import React, { useState } from "react";
import { Card, Row, Col, Button, Tooltip, Divider, Typography, Spin, InputNumber, notification } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { getProductAvailableQty } from "../../utils/product";
import useAuthStore from "../../states/useAuth";

const { Title } = Typography;

// DataItem component for displaying individual data items with edit functionality
const DataItem = ({ label, value, editable = false, tooltip, onConfirm }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleConfirm = () => {
    onConfirm(tempValue); // Call the onConfirm callback with the updated value
    setIsEditing(false);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <span style={{ marginRight: 8 }}>{label}</span>
      {tooltip && (
        <Tooltip title={tooltip}>
          <QuestionCircleOutlined style={{ color: "#1890ff", marginLeft: 4 }} />
        </Tooltip>
      )}
      <div style={{ fontSize: 24, fontWeight: "bold" }}>
        {isEditing ? (
          <InputNumber min={0} value={tempValue} onChange={(val) => setTempValue(val)} style={{ width: "100%" }} />
        ) : (
          value
        )}
      </div>
      {editable && (
        <Button
          type="primary"
          size="small"
          onClick={isEditing ? handleConfirm : handleEditClick}
          style={{ marginTop: "0.5rem" }}
        >
          {isEditing ? "Confirm" : "Edit"}
        </Button>
      )}
    </div>
  );
};

// Main InventoryCard component
const InventoryCard = ({ state = {}, isReferral, productType, isHidePrice, onChangeQty }) => {
  const { data, form, onChange } = state;
  const { auth } = useAuthStore();

  // Display a loading spinner if data is not available yet
  if (!data) {
    return (
      <Card
        style={{
          border: "2px solid var(--ion-color-step-150)",
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }

  return (
    <Card
      style={{
        border: "2px solid var(--ion-color-step-150)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={4}>Overview</Title>
          <Row gutter={[16, 16]}>
            {productType == "voucher" || productType == "giftCard" ? (
              <>
                <Col span={8}>
                  <DataItem
                    label="Total Issued"
                    value={data.total}
                    editable={true}
                    tooltip="The total quantity issued."
                    onConfirm={(val) => onChangeQty("total", val)}
                  />
                </Col>
                <Col span={8}>
                  <DataItem
                    label="Total Distributed"
                    value={data.distributed}
                    tooltip="The total quantity distributed to users."
                    onConfirm={(val) => onChangeQty("distributed", val)}
                  />
                </Col>
              </>
            ) : null}

            <Col span={8}>
              <DataItem
                label="Total Redeemed"
                value={data.redeemded}
                onConfirm={(val) => onChangeQty("redeemed", val)}
              />
            </Col>
          </Row>
        </Col>

        {productType == "voucher" || productType == "giftCard" ? (
          <>
            <Divider style={{ margin: 0 }} />
            <Col span={24}>
              <Title level={4}>Inventory Management</Title>
              <Row gutter={[16, 16]}>
                {isReferral ? (
                  <Col span={8}>
                    <DataItem
                      label="Referral Campaigns"
                      value={data.referral}
                      editable={false}
                      tooltip="The quantity reserved for platform referral campaigns."
                      onConfirm={(val) => onChangeQty("referral", val)}
                    />
                  </Col>
                ) : (
                  <>
                    <Col span={8}>
                      <DataItem
                        label="Available Qty"
                        value={getProductAvailableQty(data)}
                        onConfirm={(val) => onChangeQty("availableQty", val)}
                      />
                    </Col>
                    <Col span={8}>
                      <DataItem
                        label="For Sale"
                        value={data.sale}
                        editable={!isHidePrice}
                        onConfirm={(val) => onChangeQty("sale", val)}
                      />
                    </Col>
                    {productType == "voucher" ? (
                      <Col span={8}>
                        <DataItem
                          label="Coin Redemption"
                          value={data.estamp}
                          editable={true}
                          tooltip="The quantity reserved specifically for estamp redemptions."
                          onConfirm={(val) => onChangeQty("estamp", val)}
                        />
                      </Col>
                    ) : null}
                    {productType == "voucher" ? (
                      <Col span={8}>
                        <DataItem
                          label="Promotion Code"
                          value={data.promotionCode}
                          editable={true}
                          tooltip="The quantity reserved for promotional code redemptions."
                          onConfirm={(val) => onChangeQty("promotionCode", val)}
                        />
                      </Col>
                    ) : null}
                    {productType == "voucher" ? (
                      <Col span={8}>
                        <DataItem
                          label="Welcome Campaigns"
                          value={data.welcome}
                          editable={true}
                          tooltip="The quantity reserved for platform welcome campaigns."
                          onConfirm={(val) => onChangeQty("welcome", val)}
                        />
                      </Col>
                    ) : null}
                  </>
                )}
              </Row>
            </Col>
          </>
        ) : null}
      </Row>
    </Card>
  );
};

export default InventoryCard;
